import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, fileUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";
import SaveIndicator from "@/components/SaveIndicator";
import { useAutoSave } from "@/lib/useAutoSave";

export default function AdminSettings() {
  const { refresh } = useSettings();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
  }, []);

  const update = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const buildPayload = (snap) => {
    const payload = { ...snap };
    delete payload.id;
    delete payload.updated_at;
    if (payload.platform_fee_percentage !== undefined) {
      payload.platform_fee_percentage = Number(payload.platform_fee_percentage);
    }
    return payload;
  };

  const autoSave = useAutoSave({
    value: s,
    save: async (snap) => {
      if (!snap) return snap;
      await api.put("/settings", buildPayload(snap));
      await refresh();
      return snap;
    },
    debounceMs: 1500,
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", buildPayload(s));
      await refresh();
      autoSave.markBaseline(s);
      toast.success("institutional settings saved.");
    } catch (e) {
      toast.error("save failed.");
    } finally {
      setSaving(false);
    }
  };

  const uploadAsset = async (field, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update(field, data.path);
      toast.success("asset uploaded.");
    } catch {
      toast.error("upload failed.");
    }
  };

  if (!s) return <AdminLayout><p className="editorial-label">loading…</p></AdminLayout>;

  const renderAsset = (field, label, testid) => (
    <div className="bg-ivory border border-taupe/50 p-5">
      <p className="editorial-label mb-3">{label}</p>
      <div className="aspect-square bg-linen mb-3 overflow-hidden flex items-center justify-center">
        {s[field] ? <img src={fileUrl(s[field])} alt="" className="w-full h-full object-contain p-3" /> : <span className="font-serif text-taupe text-lg lowercase">no asset</span>}
      </div>
      <div className="flex items-center gap-2">
        <label className="flex-1 border border-taupe hover:border-brass px-4 py-2 text-[0.65rem] tracking-[0.25em] uppercase cursor-pointer flex items-center justify-center gap-2 text-charcoal" data-testid={testid}>
          <Upload className="w-3 h-3" /> upload
          <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadAsset(field, e.target.files?.[0])} />
        </label>
        {s[field] && (
          <button
            type="button"
            onClick={() => { update(field, ""); toast.success("asset removed."); }}
            className="border border-taupe hover:border-red-700 hover:text-red-700 px-3 py-2 text-charcoal"
            title="remove asset"
            aria-label="remove asset"
            data-testid={`${testid}-remove`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );

  const renderField = (k, label, type = "text", suffix) => (
    <div>
      <label className="editorial-label">{label}</label>
      <div className="flex items-center gap-2 border-b border-taupe mt-2">
        <input data-testid={`settings-${k}`} type={type} value={s[k] ?? ""} onChange={(e) => update(k, e.target.value)} className="w-full bg-transparent py-2 focus:outline-none text-charcoal" />
        {suffix && <span className="text-xs text-taupe">{suffix}</span>}
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div data-testid="admin-settings" className="max-w-5xl">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="editorial-label">institutional · settings</span>
            <h1 className="font-serif text-5xl lowercase text-charcoal mt-3">the house identity.</h1>
            <div className="mt-3">
              <SaveIndicator status={autoSave.status} lastSavedAt={autoSave.lastSavedAt} lastError={autoSave.lastError} flush={autoSave.flush} testId="settings-save-indicator" />
            </div>
          </div>
          <button onClick={save} disabled={saving} data-testid="settings-save" className="bg-brass hover:bg-espresso text-ivory px-8 py-3 text-xs tracking-[0.25em] uppercase">
            {saving ? "saving…" : "save"}
          </button>
        </div>

        {/* Assets */}
        <section className="bg-linen border border-taupe/50 p-8 mb-10">
          <h2 className="font-serif text-2xl lowercase text-charcoal mb-6">logo & asset manager</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {renderAsset("logo_path", "brand logo", "upload-logo")}
            {renderAsset("favicon_path", "favicon", "upload-favicon")}
            {renderAsset("placeholder_path", "default placeholder", "upload-placeholder")}
            {renderAsset("storefront_hero_path", "storefront hero · public homepage", "upload-storefront-hero")}
            {renderAsset("login_side_path", "studio login · side image", "upload-login-side")}
          </div>
        </section>

        <EbayConnectPanel />

        {/* Company */}
        <section className="bg-linen border border-taupe/50 p-8 mb-10">
          <h2 className="font-serif text-2xl lowercase text-charcoal mb-6">company details</h2>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
            {renderField("legal_business_name", "legal business name")}
            {renderField("support_email", "support email", "email")}
            {renderField("contact_phone", "contact phone")}
            {renderField("physical_address", "physical address")}
            {renderField("instagram_handle", "instagram handle")}
            {renderField("facebook_handle", "facebook handle")}
            {renderField("pinterest_handle", "pinterest handle")}
            {renderField("tagline", "public tagline")}
          </div>
        </section>

        {/* Global Parameters */}
        <section className="bg-linen border border-taupe/50 p-8">
          <h2 className="font-serif text-2xl lowercase text-charcoal mb-2">global parameters</h2>
          <p className="text-xs text-espresso mb-6">the platform fee % is used by the pricing engine across all pieces.</p>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <label className="editorial-label">platform fee percentage (decimal)</label>
              <input
                data-testid="settings-platform_fee_percentage"
                type="number"
                step="0.01"
                min="0"
                max="0.99"
                value={s.platform_fee_percentage}
                onChange={(e) => update("platform_fee_percentage", e.target.value)}
                className="w-full bg-transparent border-b border-taupe py-2 mt-2 focus:outline-none text-charcoal"
              />
              <p className="text-xs text-espresso mt-2">currently · {(Number(s.platform_fee_percentage) * 100).toFixed(1)}%</p>
            </div>
          </div>
        </section>

        {/* Currency & Tax */}
        <section className="bg-linen border border-taupe/50 p-8 mt-10" data-testid="currency-tax-section">
          <h2 className="font-serif text-2xl lowercase text-charcoal mb-2">currency & tax residency</h2>
          <p className="text-xs text-espresso mb-6">configure once — every price across the storefront and studio formats accordingly. tax compute branches on residency.</p>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
            <div>
              <label className="editorial-label">operating currency</label>
              <select value={s.currency_code || "USD"} onChange={(e) => update("currency_code", e.target.value)} data-testid="settings-currency_code" className="w-full bg-transparent border-b border-taupe py-2 mt-2 focus:outline-none text-charcoal">
                <option value="USD">USD · United States Dollar</option>
                <option value="EUR">EUR · Euro</option>
                <option value="GBP">GBP · Pound Sterling</option>
                <option value="BRL">BRL · Brazilian Real</option>
                <option value="CAD">CAD · Canadian Dollar</option>
                <option value="AUD">AUD · Australian Dollar</option>
                <option value="CHF">CHF · Swiss Franc</option>
              </select>
            </div>
            <div>
              <label className="editorial-label">tax residency</label>
              <select value={s.tax_residency || "US"} onChange={(e) => update("tax_residency", e.target.value)} data-testid="settings-tax_residency" className="w-full bg-transparent border-b border-taupe py-2 mt-2 focus:outline-none text-charcoal">
                <option value="US">United States · sales tax (nexus)</option>
                <option value="EU">European Union · VAT (per-country)</option>
              </select>
            </div>
            <div>
              <label className="editorial-label">unit system</label>
              <select value={s.unit_system || "metric"} onChange={(e) => update("unit_system", e.target.value)} data-testid="settings-unit_system" className="w-full bg-transparent border-b border-taupe py-2 mt-2 focus:outline-none text-charcoal">
                <option value="metric">metric · kg / cm</option>
                <option value="imperial">imperial · lb / in</option>
              </select>
              <p className="text-[0.65rem] text-taupe italic mt-2">applies across inventory, storefront, and product specs. weight is always stored in kg internally — display + input units follow this setting.</p>
            </div>
          </div>

          {s.tax_residency === "US" ? (
            <div className="grid md:grid-cols-3 gap-6 mt-6" data-testid="us-tax-config">
              <div>
                <label className="editorial-label">home state · nexus</label>
                <input value={s.home_state || ""} onChange={(e) => update("home_state", e.target.value.toUpperCase())} maxLength={2} data-testid="settings-home_state" placeholder="NY" className="w-full bg-transparent border-b border-taupe py-2 mt-2 focus:outline-none focus:border-brass text-charcoal uppercase" />
              </div>
              <div>
                <label className="editorial-label">state tax rate %</label>
                <input type="number" step="0.001" value={s.state_tax_rate || 0} onChange={(e) => update("state_tax_rate", Number(e.target.value))} data-testid="settings-state_tax_rate" className="w-full bg-transparent border-b border-taupe py-2 mt-2 focus:outline-none focus:border-brass text-charcoal" />
              </div>
              <div>
                <label className="editorial-label">label shown on receipts</label>
                <input value={s.sales_tax_label || "Sales Tax"} onChange={(e) => update("sales_tax_label", e.target.value)} data-testid="settings-sales_tax_label" className="w-full bg-transparent border-b border-taupe py-2 mt-2 focus:outline-none focus:border-brass text-charcoal" />
              </div>
              <p className="md:col-span-3 text-xs text-espresso">sales tax is charged only on shipments to your home state ({s.home_state || "—"}). other us states & international ship tax-free.</p>
            </div>
          ) : (
            <div className="mt-6 bg-ivory border border-taupe/40 p-5" data-testid="eu-tax-config">
              <p className="editorial-label mb-3">vat rates by country (editable)</p>
              <p className="text-xs text-espresso mb-3">currently {Object.keys(s.vat_rates || {}).length} countries configured · {s.vat_rates?.FR ?? 20}% for FR, {s.vat_rates?.DE ?? 19}% for DE, etc. detailed editor coming soon — for now adjust via the api.</p>
            </div>
          )}
        </section>

        {/* Accounting & Tax (US / Schedule C) */}
        <section className="bg-linen border border-taupe/50 p-8 mt-10" data-testid="accounting-tax-section">
          <h2 className="font-serif text-2xl lowercase text-charcoal mb-2">accounting & tax</h2>
          <p className="text-xs text-espresso mb-6">used by the schedule c summary, sales tax tracker, mileage deduction and home office calculator.</p>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
            <div>
              <label className="editorial-label">entity type</label>
              <select value={s.entity_type || "undecided"} onChange={(e) => update("entity_type", e.target.value)} data-testid="settings-entity_type" className="w-full bg-transparent border-b border-taupe py-2 mt-2 focus:outline-none text-charcoal">
                <option value="undecided">undecided</option>
                <option value="sole_prop">sole proprietorship · schedule c</option>
                <option value="smllc">single-member llc (disregarded) · schedule c</option>
                <option value="s_corp">s-corp election · form 1120-s</option>
              </select>
            </div>
            {renderField("ein", "federal ein (optional)")}
            {renderField("ga_sales_tax_number", "georgia sales & use tax #")}
            {renderField("business_license_number", "cobb county occupation tax certificate #")}
            <div>
              <label className="editorial-label">default state</label>
              <input value={s.default_state || "GA"} onChange={(e) => update("default_state", e.target.value.toUpperCase())} maxLength={2} className="w-full bg-transparent border-b border-taupe py-2 mt-2 focus:outline-none text-charcoal uppercase" data-testid="settings-default_state" />
            </div>
            {renderField("default_county", "default county")}
            <div>
              <label className="editorial-label">default local tax rate (%)</label>
              <input type="number" step="0.01" value={s.default_local_tax_rate ?? 7} onChange={(e) => update("default_local_tax_rate", Number(e.target.value))} className="w-full bg-transparent border-b border-taupe py-2 mt-2 focus:outline-none text-charcoal" data-testid="settings-default_local_tax_rate" />
            </div>
            <div>
              <label className="editorial-label">irs standard mileage rate ($/mile)</label>
              <input type="number" step="0.001" value={s.irs_mileage_rate ?? 0.67} onChange={(e) => update("irs_mileage_rate", Number(e.target.value))} className="w-full bg-transparent border-b border-taupe py-2 mt-2 focus:outline-none text-charcoal" data-testid="settings-irs_mileage_rate" />
            </div>
            <div>
              <label className="editorial-label">business license renewal month</label>
              <input type="number" min="1" max="12" value={s.business_license_renewal_month ?? 1} onChange={(e) => update("business_license_renewal_month", Number(e.target.value))} className="w-full bg-transparent border-b border-taupe py-2 mt-2 focus:outline-none text-charcoal" data-testid="settings-license_month" />
            </div>
          </div>

          <h3 className="font-serif text-xl lowercase text-charcoal mt-8 mb-2">home office · inventory storage</h3>
          <p className="text-xs text-espresso mb-5">used by the actual-method and simplified-method deduction calculators. confirm eligibility with a cpa — irc §280a(c)(2).</p>
          <div className="grid md:grid-cols-3 gap-x-8 gap-y-6">
            {[
              ["home_total_sqft", "total home sqft"],
              ["home_office_sqft", "business office sqft"],
              ["home_storage_sqft", "inventory storage sqft"],
              ["home_monthly_rent_or_mortgage", "monthly rent or mortgage interest"],
              ["home_monthly_utilities", "monthly utilities"],
              ["home_monthly_internet", "monthly internet"],
              ["home_monthly_insurance", "monthly insurance"],
              ["home_monthly_repairs", "monthly repairs"],
            ].map(([k, label]) => (
              <div key={k}>
                <label className="editorial-label">{label}</label>
                <input type="number" step="0.01" value={s[k] ?? 0} onChange={(e) => update(k, Number(e.target.value))} className="w-full bg-transparent border-b border-taupe py-2 mt-2 focus:outline-none text-charcoal" data-testid={`settings-${k}`} />
              </div>
            ))}
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}

// ---------------- eBay connect panel ----------------
function EbayConnectPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/ebay/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
  useEffect(() => {
    load();
    // Handle return from OAuth flow (eBay redirected admin back with ?ebay=connected)
    const params = new URLSearchParams(window.location.search);
    if (params.get("ebay") === "connected") {
      toast.success("ebay connected — ready to publish listings.");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("ebay") === "error") {
      toast.error(`ebay connect failed: ${params.get("msg") || "unknown error"}`);
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("ebay") === "declined") {
      toast.warning("ebay authorization was declined.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/ebay/connect");
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not start ebay connect.");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("disconnect eBay? you'll need to re-authorize to publish listings again.")) return;
    setBusy(true);
    try {
      await api.post("/ebay/disconnect");
      toast.success("ebay disconnected.");
      load();
    } catch { toast.error("could not disconnect."); } finally { setBusy(false); }
  };

  if (!status) return null;

  return (
    <section className="bg-linen border border-taupe/50 p-8 mb-10" data-testid="ebay-connect-panel">
      <h2 className="font-serif text-2xl lowercase text-charcoal mb-2">ebay · cross-posting</h2>
      <p className="text-xs text-espresso mb-6">connect your ebay seller account to publish maison pilet pieces as listings.</p>

      {!status.configured ? (
        <p className="text-sm text-amber-800">⚠ developer credentials missing on the server. set EBAY_APP_ID, EBAY_CERT_ID, EBAY_RU_NAME in backend .env.</p>
      ) : status.connected ? (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-700" />
            <span className="text-sm text-charcoal">connected · {status.env === "production" ? "production" : "sandbox"} · marketplace {status.marketplace}</span>
          </div>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-2 text-xs text-espresso mb-4">
            <div><span className="editorial-label">payment policy</span><br />{status.payment_policy_id || <span className="text-red-700">— missing —</span>}</div>
            <div><span className="editorial-label">return policy</span><br />{status.return_policy_id || <span className="text-red-700">— missing —</span>}</div>
            <div><span className="editorial-label">fulfillment policy</span><br />{status.fulfillment_policy_id || <span className="text-red-700">— missing —</span>}</div>
            <div><span className="editorial-label">default category id</span><br />{status.default_category_id}</div>
          </div>

          {(!status.payment_policy_id || !status.return_policy_id || !status.fulfillment_policy_id) && (
            <div className="bg-amber-50/60 border border-amber-700/40 px-4 py-3 mb-5 text-xs text-espresso">
              <p className="font-medium text-amber-900 mb-2">⚠ one or more business policies are missing.</p>
              <p className="mb-3">click below to create them now — this also opts the seller into eBay&apos;s Selling Policy Management program if needed.</p>
              <RecreatePoliciesButton onDone={load} />
            </div>
          )}

          <EbayPolicyEditor onApplied={load} />

          <EbayHealthCheck />

          <EbaySyncLog />

          <div className="flex flex-wrap gap-3 mt-6">
            <button onClick={disconnect} disabled={busy} className="px-4 py-2 border border-red-700 text-red-700 hover:bg-red-700 hover:text-ivory text-xs uppercase tracking-widest" data-testid="ebay-disconnect">
              disconnect ebay
            </button>
            <EbayResetCacheButton onDone={load} />
          </div>
        </div>
      ) : (
        <div>
          <p className="text-sm text-espresso mb-4">click below to authorize maison pilet against your ebay <strong>{status.env}</strong> seller account. you&apos;ll be redirected to ebay&apos;s login.</p>
          <button onClick={connect} disabled={busy} className="bg-charcoal text-ivory hover:bg-brass px-5 py-2 text-xs uppercase tracking-widest" data-testid="ebay-connect">
            {busy ? "redirecting…" : `connect ebay (${status.env})`}
          </button>
        </div>
      )}
    </section>
  );
}

// ---------------- eBay live health check (proactive diagnostic) ----------------
function EbayHealthCheck() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.get("/ebay/health");
      setResult(data);
      if (data.ok) toast.success("ebay connection healthy · all checks passed.");
      else toast.warning("ebay connection has issues — see details below.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "health check failed.");
    } finally {
      setBusy(false);
    }
  };

  const statusColor = (s) => s === "pass" ? "text-emerald-700" : s === "fail" ? "text-red-700" : "text-amber-800";
  const statusSymbol = (s) => s === "pass" ? "✓" : s === "fail" ? "✗" : "○";

  return (
    <div className="mt-6 border-t border-taupe/40 pt-5" data-testid="ebay-health-panel">
      <div className="flex items-center justify-between mb-3">
        <span className="editorial-label">connection diagnostic</span>
        <button
          onClick={run}
          disabled={busy}
          className="px-4 py-1.5 border border-charcoal text-charcoal hover:bg-charcoal hover:text-ivory text-[10px] uppercase tracking-widest disabled:opacity-50"
          data-testid="ebay-test-connection"
        >
          {busy ? "testing…" : "test ebay connection"}
        </button>
      </div>
      {result && (
        <div className="bg-ivory border border-taupe/50 px-4 py-3 text-xs space-y-1.5" data-testid="ebay-health-results">
          {result.checks.map((c) => (
            <div key={c.name} className="flex gap-3">
              <span className={`${statusColor(c.status)} font-mono w-3`}>{statusSymbol(c.status)}</span>
              <span className="w-40 text-espresso">{c.name.replace(/_/g, " ")}</span>
              <span className={`${statusColor(c.status)} flex-1`}>{c.detail || c.status}</span>
            </div>
          ))}
          <p className={`text-[11px] uppercase tracking-widest mt-2 pt-2 border-t border-taupe/40 ${result.ok ? "text-emerald-700" : "text-red-700"}`}>
            overall · {result.ok ? "healthy" : "issues detected"}
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------- Reset all env-linked eBay cache (nuclear option) ----------------
function EbayResetCacheButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!window.confirm("Reset eBay cache? This wipes refresh token, policies, location, and default category — you'll need to Connect eBay again to publish listings. Use this when switching sandbox ↔ production.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ebay/reset_cache");
      toast.success(data.message || "ebay cache reset.");
      if (onDone) onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not reset cache.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={run}
      disabled={busy}
      className="px-4 py-2 border border-amber-800 text-amber-900 hover:bg-amber-800 hover:text-ivory text-xs uppercase tracking-widest disabled:opacity-50"
      data-testid="ebay-reset-cache"
    >
      {busy ? "resetting…" : "reset ebay cache"}
    </button>
  );
}

// ---------------- Sync attempts log (retroactive diagnostic) ----------------
function EbaySyncLog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ebay/sync_log?limit=50");
      setEntries(data.entries || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not load sync log.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const clearLog = async () => {
    if (!window.confirm("Delete all sync log entries? This does not affect actual eBay listings.")) return;
    setBusy(true);
    try {
      const { data } = await api.delete("/ebay/sync_log");
      toast.success(`cleared ${data.deleted} entries.`);
      setEntries([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not clear log.");
    } finally {
      setBusy(false);
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="mt-6 border-t border-taupe/40 pt-5" data-testid="ebay-sync-log-panel">
      <div className="flex items-center justify-between mb-3">
        <span className="editorial-label">recent sync attempts · last 50</span>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading} className="px-3 py-1 border border-charcoal text-charcoal hover:bg-charcoal hover:text-ivory text-[10px] uppercase tracking-widest disabled:opacity-50" data-testid="ebay-sync-log-refresh">
            {loading ? "refreshing…" : "refresh"}
          </button>
          {entries.length > 0 && (
            <button onClick={clearLog} disabled={busy} className="px-3 py-1 border border-red-700 text-red-700 hover:bg-red-700 hover:text-ivory text-[10px] uppercase tracking-widest disabled:opacity-50" data-testid="ebay-sync-log-clear">
              clear
            </button>
          )}
        </div>
      </div>

      {entries.length === 0 && !loading && (
        <p className="text-xs text-espresso/70 italic">no sync attempts logged yet. cross-post any inventory item and the attempt will appear here.</p>
      )}

      {entries.length > 0 && (
        <div className="bg-ivory border border-taupe/40 divide-y divide-taupe/30" data-testid="ebay-sync-log-list">
          {entries.map((e) => {
            const isOpen = expandedId === e.id;
            const isSuccess = e.status === "success";
            return (
              <div key={e.id} className="text-xs">
                <button
                  onClick={() => setExpandedId(isOpen ? null : e.id)}
                  className="w-full px-3 py-2 flex items-center gap-3 hover:bg-taupe/10 text-left"
                  data-testid={`ebay-sync-log-row-${e.id}`}
                >
                  <span className={`font-mono w-3 ${isSuccess ? "text-emerald-700" : "text-red-700"}`}>{isSuccess ? "✓" : "✗"}</span>
                  <span className="text-espresso/60 w-24 font-mono">{fmtTime(e.attempted_at)}</span>
                  <span className="text-charcoal uppercase w-10 text-[10px] tracking-widest">{e.action}</span>
                  <span className="text-charcoal font-mono w-16">{e.sku || "—"}</span>
                  <span className="flex-1 truncate">{e.title || "(untitled)"}</span>
                  <span className={`text-[10px] ${isSuccess ? "text-emerald-700" : "text-red-700"}`}>
                    {isSuccess ? (e.listing_id ? `#${e.listing_id}` : "ok") : `${e.error_status || "err"}`}
                  </span>
                  <span className="text-espresso/50 w-14 text-right">{e.duration_ms ? `${e.duration_ms}ms` : ""}</span>
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 pt-1 bg-taupe/5 text-[11px] space-y-1" data-testid={`ebay-sync-log-detail-${e.id}`}>
                    <div><span className="text-espresso/60">item · </span>{e.item_id} · {e.sku}</div>
                    {e.attempted_by && <div><span className="text-espresso/60">by · </span>{e.attempted_by}</div>}
                    {e.price != null && <div><span className="text-espresso/60">price · </span>${Number(e.price).toFixed(2)}</div>}
                    {e.image_count != null && <div><span className="text-espresso/60">images · </span>{e.image_count}</div>}
                    {isSuccess && e.listing_url && (
                      <div>
                        <span className="text-espresso/60">url · </span>
                        <a href={e.listing_url} target="_blank" rel="noreferrer" className="text-brass underline break-all">{e.listing_url}</a>
                      </div>
                    )}
                    {isSuccess && e.offer_id && <div><span className="text-espresso/60">offer id · </span>{e.offer_id}</div>}
                    {!isSuccess && e.error_detail && (
                      <div>
                        <span className="text-espresso/60">error · </span>
                        <pre className="mt-1 bg-red-50 border border-red-700/30 p-2 whitespace-pre-wrap text-red-900 text-[10px]">{e.error_detail}</pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------- Recreate policies button (manual fallback when auto-create silently failed) ----------------
function RecreatePoliciesButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [errorDetail, setErrorDetail] = useState(null);
  const run = async () => {
    setBusy(true);
    setErrorDetail(null);
    try {
      const { data } = await api.post("/ebay/recreate_policies");
      toast.success(`policies created · payment ${data.policies.payment ? "✓" : "✗"} · return ${data.policies.return ? "✓" : "✗"} · fulfillment ${data.policies.fulfillment ? "✓" : "✗"}`);
      if (onDone) onDone();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "unknown error";
      setErrorDetail(typeof msg === "string" ? msg : JSON.stringify(msg, null, 2));
      toast.error("policy creation failed — see details below.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <button onClick={run} disabled={busy} className="bg-amber-900 text-ivory hover:bg-amber-800 px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-50" data-testid="ebay-recreate-policies">
        {busy ? "creating policies…" : "create / recreate policies now"}
      </button>
      {errorDetail && (
        <pre className="mt-3 text-[0.65rem] bg-ivory border border-red-700/40 p-3 overflow-x-auto whitespace-pre-wrap text-red-900">{errorDetail}</pre>
      )}
    </div>
  );
}

// ---------------- eBay editable policy defaults ----------------
function EbayPolicyEditor({ onApplied }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setS({ ...s, [k]: v });

  const apply = async () => {
    setBusy(true);
    try {
      // Persist editable defaults — the /settings PUT auto-pushes ebay-relevant fields
      // to eBay in the same request (no separate /apply_policy_changes needed).
      const mode = s.ebay_default_shipping_mode || "calculated";
      const enabledServices = s.ebay_shipping_services || ["USPSGroundAdvantage"];
      await api.put("/settings", {
        ebay_default_handling_days: Number(s.ebay_default_handling_days || 1),
        ebay_default_return_window_days: Number(s.ebay_default_return_window_days || 30),
        ebay_default_return_shipping_payer: s.ebay_default_return_shipping_payer || "BUYER",
        ebay_default_shipping_mode: mode,
        ebay_default_handling_cost: Number(s.ebay_default_handling_cost || 0),
        ebay_shipping_services: enabledServices,
        // legacy flat-rate fields, still stored for fallback
        ebay_default_shipping_cost: Number(s.ebay_default_shipping_cost || 0),
        ebay_default_shipping_service: s.ebay_default_shipping_service || "USPSGroundAdvantage",
      });
      toast.success("saved · policy pushed to ebay.");
      if (onApplied) onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not apply policy changes.");
    } finally {
      setBusy(false);
    }
  };

  const toggleService = (code) => {
    const cur = s.ebay_shipping_services || ["USPSGroundAdvantage"];
    const next = cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code];
    if (!next.length) {
      toast.error("keep at least one shipping service enabled.");
      return;
    }
    set("ebay_shipping_services", next);
  };

  if (!s) return null;
  const mode = s.ebay_default_shipping_mode || "calculated";
  const enabledServices = s.ebay_shipping_services || ["USPSGroundAdvantage"];
  const CALCULATED_SERVICES = [
    { code: "USPSPriorityMailExpress", label: "USPS Priority Mail Express (1–2 day)" },
    { code: "USPSPriority", label: "USPS Priority Mail (1–3 day)" },
    { code: "USPSGroundAdvantage", label: "USPS Ground Advantage (2–5 day, budget)" },
  ];

  return (
    <div className="border-t border-taupe/30 pt-5" data-testid="ebay-policy-editor">
      <p className="editorial-label mb-3">policy defaults · tune & push to ebay</p>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4 mb-4">
        <label className="flex flex-col">
          <span className="editorial-label mb-1">handling days</span>
          <input type="number" min="1" max="30" value={s.ebay_default_handling_days ?? 1} onChange={(e) => set("ebay_default_handling_days", e.target.value)} className="border border-taupe/40 px-3 py-2 bg-white" data-testid="ebay-handling-days" />
        </label>
        <label className="flex flex-col">
          <span className="editorial-label mb-1">return window (days)</span>
          <select value={s.ebay_default_return_window_days ?? 30} onChange={(e) => set("ebay_default_return_window_days", Number(e.target.value))} className="border border-taupe/40 px-3 py-2 bg-white" data-testid="ebay-return-window">
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
          </select>
        </label>
        <label className="flex flex-col">
          <span className="editorial-label mb-1">who pays return shipping</span>
          <select value={s.ebay_default_return_shipping_payer || "BUYER"} onChange={(e) => set("ebay_default_return_shipping_payer", e.target.value)} className="border border-taupe/40 px-3 py-2 bg-white" data-testid="ebay-return-payer">
            <option value="BUYER">buyer</option>
            <option value="SELLER">seller (free returns)</option>
          </select>
        </label>
        <label className="flex flex-col">
          <span className="editorial-label mb-1">shipping mode</span>
          <select value={mode} onChange={(e) => set("ebay_default_shipping_mode", e.target.value)} className="border border-taupe/40 px-3 py-2 bg-white" data-testid="ebay-shipping-mode">
            <option value="calculated">calculated (live USPS rates)</option>
            <option value="flat">flat rate</option>
          </select>
        </label>
        {mode === "calculated" ? (
          <label className="flex flex-col">
            <span className="editorial-label mb-1">handling cost ($)</span>
            <input type="number" step="0.01" min="0" value={s.ebay_default_handling_cost ?? 0} onChange={(e) => set("ebay_default_handling_cost", e.target.value)} className="border border-taupe/40 px-3 py-2 bg-white" data-testid="ebay-handling-cost" />
          </label>
        ) : (
          <>
            <label className="flex flex-col">
              <span className="editorial-label mb-1">flat shipping cost ($)</span>
              <input type="number" step="0.01" min="0" value={s.ebay_default_shipping_cost ?? 10} onChange={(e) => set("ebay_default_shipping_cost", e.target.value)} className="border border-taupe/40 px-3 py-2 bg-white" data-testid="ebay-shipping-cost" />
            </label>
            <label className="flex flex-col">
              <span className="editorial-label mb-1">flat shipping service</span>
              <select value={s.ebay_default_shipping_service || "USPSPriority"} onChange={(e) => set("ebay_default_shipping_service", e.target.value)} className="border border-taupe/40 px-3 py-2 bg-white" data-testid="ebay-shipping-service">
                <option value="USPSPriority">USPS Priority Mail</option>
                <option value="USPSPriorityFlatRateBox">USPS Priority Flat-Rate Box</option>
                <option value="USPSGroundAdvantage">USPS Ground Advantage</option>
                <option value="USPSMedia">USPS Media Mail</option>
              </select>
            </label>
          </>
        )}
      </div>
      {mode === "calculated" && (
        <div className="mb-4" data-testid="ebay-calculated-services-panel">
          <p className="editorial-label mb-2">enabled buyer options (calculated rates)</p>
          <div className="flex flex-wrap gap-2">
            {CALCULATED_SERVICES.map((svc) => {
              const on = enabledServices.includes(svc.code);
              return (
                <button
                  key={svc.code}
                  type="button"
                  onClick={() => toggleService(svc.code)}
                  data-testid={`ebay-service-toggle-${svc.code}`}
                  className={`px-3 py-1.5 text-xs border transition-colors ${on ? "bg-charcoal text-ivory border-charcoal" : "bg-white text-espresso border-taupe/50 hover:border-charcoal"}`}
                >
                  {on ? "✓ " : ""}{svc.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <button onClick={apply} disabled={busy} className="bg-charcoal text-ivory hover:bg-brass px-5 py-2 text-xs uppercase tracking-widest disabled:opacity-50" data-testid="ebay-apply-policies">
        {busy ? "applying…" : "save & push to ebay"}
      </button>
      <p className="text-[0.65rem] text-taupe mt-3 tracking-widest uppercase">
        saving auto-pushes changes to your shared eBay fulfillment policy — all live listings inherit the new terms immediately. calculated rates require accurate per-item weight + dimensions.
      </p>
    </div>
  );
}
